import * as React from "react";
import { cn } from "@/lib/utils";
import { theme } from "@/lib/theme";

/**
 * Table component with design system integration
 * Based on Catalyst UI Kit patterns
 * Supports responsive behavior, striped rows, grid, and density options
 */

interface TableContextValue {
  bleed: boolean;
  dense: boolean;
  grid: boolean;
  striped: boolean;
}

const TableContext = React.createContext<TableContextValue>({
  bleed: false,
  dense: false,
  grid: false,
  striped: false,
});

export interface TableProps extends React.HTMLAttributes<HTMLDivElement> {
  bleed?: boolean;
  dense?: boolean;
  grid?: boolean;
  striped?: boolean;
}

export function Table({
  bleed = false,
  dense = false,
  grid = false,
  striped = false,
  className,
  children,
  ...props
}: TableProps) {
  return (
    <TableContext.Provider value={{ bleed, dense, grid, striped }}>
      <div className="flow-root">
        <div
          {...props}
          className={cn("overflow-x-auto whitespace-nowrap", !bleed && "-mx-4 sm:mx-0", className)}
        >
          <div className={cn("inline-block min-w-full align-middle", !bleed && "px-4 sm:px-0")}>
            <table className="min-w-full text-left text-sm">{children}</table>
          </div>
        </div>
      </div>
    </TableContext.Provider>
  );
}

export interface TableHeaderProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

export const TableHeader = React.forwardRef<HTMLTableSectionElement, TableHeaderProps>(
  ({ className, ...props }, ref) => {
    return <thead ref={ref} className={cn("text-muted-foreground", className)} {...props} />;
  },
);
TableHeader.displayName = "TableHeader";

export interface TableBodyProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

export const TableBody = React.forwardRef<HTMLTableSectionElement, TableBodyProps>((props, ref) => {
  return <tbody ref={ref} {...props} />;
});
TableBody.displayName = "TableBody";

interface TableRowContextValue {
  href?: string;
  target?: string;
  title?: string;
}

const TableRowContext = React.createContext<TableRowContextValue>({
  href: undefined,
  target: undefined,
  title: undefined,
});

export interface TableRowProps extends React.HTMLAttributes<HTMLTableRowElement> {
  href?: string;
  target?: string;
  title?: string;
}

export const TableRow = React.forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ href, target, title, className, children, ...props }, ref) => {
    const { striped } = React.useContext(TableContext);

    return (
      <TableRowContext.Provider value={{ href, target, title }}>
        <tr
          ref={ref}
          className={cn(
            className,
            href && "relative cursor-pointer",
            striped && "even:bg-muted/50",
            href && striped && "hover:bg-muted/80 dark:hover:bg-muted/20",
            href && !striped && "hover:bg-muted/50 dark:hover:bg-muted/10",
            theme.transition.DEFAULT,
          )}
          {...props}
        >
          {href && (
            <td className="!p-0 sr-only" aria-hidden="false">
              <a
                href={href}
                target={target}
                aria-label={title}
                tabIndex={0}
                className="absolute inset-0 z-10 focus:outline-2 focus:-outline-offset-2 focus:outline-primary"
              />
            </td>
          )}
          {children}
        </tr>
      </TableRowContext.Provider>
    );
  },
);
TableRow.displayName = "TableRow";

export interface TableHeadProps extends React.ThHTMLAttributes<HTMLTableCellElement> {}

export const TableHead = React.forwardRef<HTMLTableCellElement, TableHeadProps>(
  ({ className, ...props }, ref) => {
    const { bleed, grid } = React.useContext(TableContext);

    return (
      <th
        ref={ref}
        className={cn(
          "border-b border-border px-4 py-2 font-medium text-muted-foreground",
          "first:pl-6 last:pr-6",
          grid && "border-l border-border first:border-l-0",
          !bleed && "sm:first:pl-4 sm:last:pr-4",
          className,
        )}
        {...props}
      />
    );
  },
);
TableHead.displayName = "TableHead";

export interface TableCellProps extends React.TdHTMLAttributes<HTMLTableCellElement> {}

export const TableCell = React.forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, children, ...props }, ref) => {
    const { bleed, dense, grid, striped } = React.useContext(TableContext);
    const { href } = React.useContext(TableRowContext);

    return (
      <td
        ref={ref}
        className={cn(
          href && "relative",
          "px-4",
          "first:pl-6 last:pr-6",
          !striped && "border-b border-border/50",
          grid && "border-l border-border/50 first:border-l-0",
          dense ? "py-2" : "py-3",
          !bleed && "sm:first:pl-4 sm:last:pr-4",
          className,
        )}
        {...props}
      >
        {children}
      </td>
    );
  },
);
TableCell.displayName = "TableCell";

export interface TableFooterProps extends React.HTMLAttributes<HTMLTableSectionElement> {}

export const TableFooter = React.forwardRef<HTMLTableSectionElement, TableFooterProps>(
  ({ className, ...props }, ref) => {
    return (
      <tfoot
        ref={ref}
        className={cn("border-t border-border bg-muted/50 font-medium", className)}
        {...props}
      />
    );
  },
);
TableFooter.displayName = "TableFooter";

export interface TableCaptionProps extends React.HTMLAttributes<HTMLTableCaptionElement> {}

export const TableCaption = React.forwardRef<HTMLTableCaptionElement, TableCaptionProps>(
  ({ className, ...props }, ref) => {
    return (
      <caption
        ref={ref}
        className={cn("mt-4 text-sm text-muted-foreground", className)}
        {...props}
      />
    );
  },
);
TableCaption.displayName = "TableCaption";
